/**
 * ProjectMembersView - Admin member management view
 * 
 * Allows project admins to:
 * - View all project members with their roles
 * - Add new members by email
 * - Change member roles
 * - Remove members from the project
 */

import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { FloatingActionButton, GlassPanel, Button, Icon } from '../components/common'
import { LifetimePrompt } from '../components/Payment'
import { useAuth } from '../context/AuthContext'
import { useProject } from '../context/ProjectContext'
import { useMonetizationState } from '../hooks/useMonetizationState'
import {
  getProjectMembers,
  addProjectMember,
  updateProjectMemberRole,
  removeProjectMember,
  PROJECT_ROLES,
} from '../services/projects'
import { getUserByEmail, getUserProfiles } from '../services/users'
import { ROLE_LABELS, ROLE_DESCRIPTIONS, getDefaultRole, ROLES } from '../config/permissions'
import './ProjectMembersView.css'

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Container variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
}

// Row animation variants
const rowVariants = {
  hidden: { 
    opacity: 0, 
    x: -20,
  },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: {
      duration: 0.25,
      ease: [0.4, 0, 0.2, 1],
    },
  },
}

/**
 * Format a Firestore timestamp for display
 */
const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Get role badge class
 */
const getRoleBadgeClass = (role) => {
  switch (role) {
    case PROJECT_ROLES.ADMIN:
      return 'members-view__role-badge--admin'
    case PROJECT_ROLES.GUEST:
      return 'members-view__role-badge--guest'
    case PROJECT_ROLES.CUSTOM:
      return 'members-view__role-badge--custom'
    default:
      return 'members-view__role-badge--guest'
  }
}

/**
 * MemberRow - Individual member display with actions
 */
function MemberRow({ 
  member, 
  userProfile, 
  isCurrentUser, 
  isProjectCreator,
  onRoleChange, 
  onRemove,
  loading,
}) {
  const [confirmRemove, setConfirmRemove] = useState(false)
  
  const handleRemoveClick = () => {
    if (confirmRemove) {
      onRemove(member.userId)
      setConfirmRemove(false)
    } else {
      setConfirmRemove(true)
    }
  }

  // Reset confirmation on blur
  const handleBlur = () => {
    setTimeout(() => setConfirmRemove(false), 200)
  }

  return (
    <motion.div 
      className="members-view__row"
      variants={rowVariants}
    >
      {/* Avatar */}
      <div className="members-view__avatar">
        {userProfile?.photoURL ? (
          <img src={userProfile.photoURL} alt="" />
        ) : (
          <span>{(userProfile?.displayName || userProfile?.email || '?')[0].toUpperCase()}</span>
        )}
      </div>

      {/* User Info */}
      <div className="members-view__user-info">
        <span className="members-view__user-name">
          {userProfile?.displayName || 'Unknown User'}
          {isCurrentUser && <span className="members-view__you-badge">You</span>}
          {isProjectCreator && <span className="members-view__creator-badge">Creator</span>}
        </span>
        <span className="members-view__user-email">
          {userProfile?.email || member.userId}
        </span>
      </div>

      {/* Role */}
      <div className="members-view__role-cell">
        {isProjectCreator ? (
          // Project creator cannot have their role changed
          <span className={`members-view__role-badge ${getRoleBadgeClass(member.role)}`}>
            {ROLE_LABELS[member.role] || member.role}
          </span>
        ) : (
          <select
            value={member.role}
            onChange={(e) => onRoleChange(member.userId, e.target.value)}
            disabled={loading || isCurrentUser}
            className="members-view__role-select"
            title={isCurrentUser ? "You cannot change your own role" : `Change role to ${ROLE_LABELS[member.role]}`}
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {ROLE_LABELS[role]}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Added Date */}
      <div className="members-view__date-cell">
        {formatDate(member.addedAt)}
      </div>

      {/* Actions */}
      <div className="members-view__actions-cell">
        {!isProjectCreator && !isCurrentUser && (
          <button
            className={`members-view__remove-btn ${confirmRemove ? 'members-view__remove-btn--confirm' : ''}`}
            onClick={handleRemoveClick}
            onBlur={handleBlur}
            disabled={loading}
            title={confirmRemove ? 'Click again to confirm removal' : 'Remove member'}
          >
            {confirmRemove ? 'Confirm?' : <Icon name="close" size={14} />}
          </button>
        )}
      </div>
    </motion.div>
  )
}

/**
 * AddMemberModal - Modal for adding new members
 */
function AddMemberModal({ isOpen, onClose, onAdd, existingMemberIds }) {
  const { user } = useAuth()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState(getDefaultRole())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('')
      setRole(getDefaultRole())
      setError(null)
    }
  }, [isOpen])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const trimmedEmail = email.trim().toLowerCase()
    
    if (!trimmedEmail) {
      setError('Email is required')
      return
    }

    // Basic email validation
    if (!trimmedEmail.includes('@') || !trimmedEmail.includes('.')) {
      setError('Please enter a valid email address')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Look up user by email
      const foundUser = await getUserByEmail(trimmedEmail)
      
      if (!foundUser) {
        setError('No user found with this email. They must sign up first.')
        setLoading(false)
        return
      }

      // Check if already a member
      if (existingMemberIds.includes(foundUser.id)) {
        setError('This user is already a member of this project')
        setLoading(false)
        return
      }

      // Add the member
      await onAdd(foundUser.id, role)
      onClose()
    } catch (err) {
      console.error('Failed to add member:', err)
      setError(err.message || 'Failed to add member')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="members-view__modal-overlay" onClick={onClose}>
      <motion.div 
        className="members-view__modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.2 }}
      >
        <GlassPanel glow="cyan" padding="xl">
          <h2 className="members-view__modal-title">Add Member</h2>
          
          <form onSubmit={handleSubmit} className="members-view__modal-form">
            <div className="members-view__form-group">
              <label htmlFor="member-email" className="members-view__label">
                Email Address *
              </label>
              <input
                ref={inputRef}
                id="member-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="member@example.com"
                className="members-view__input"
                disabled={loading}
              />
              <p className="members-view__hint">
                The user must have an account to be added
              </p>
            </div>

            <div className="members-view__form-group">
              <label htmlFor="member-role" className="members-view__label">
                Role
              </label>
              <select
                id="member-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="members-view__select"
                disabled={loading}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]} — {ROLE_DESCRIPTIONS[r]}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <p className="members-view__error">{error}</p>
            )}

            <div className="members-view__modal-actions">
              <Button 
                variant="ghost" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                loading={loading}
              >
                Add Member
              </Button>
            </div>
          </form>
        </GlassPanel>
      </motion.div>
    </div>
  )
}

/**
 * AccessDenied - Shown when user doesn't have permission
 */
function AccessDenied() {
  return (
    <motion.div 
      className="members-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <GlassPanel glow="amber" padding="xl" className="members-view__access-denied">
        <div className="members-view__access-denied-icon"><Icon name="lock" size={48} /></div>
        <h2>Access Denied</h2>
        <p>You don't have permission to manage members in this project.</p>
        <p className="members-view__access-denied-hint">
          Only project admins can add, remove, or modify member roles.
        </p>
      </GlassPanel>
    </motion.div>
  )
}

/**
 * ProjectMembersView - Main view component
 */
export default function ProjectMembersView() {
  const { user } = useAuth()
  const { projectId, project, can } = useProject()
  
  // Monetization state for lifetime prompt (shows once per user, permanent)
  const {
    shouldShowMembersPrompt,
    dismissMembersPrompt,
    markMembersTabVisited,
    loading: monetizationLoading,
  } = useMonetizationState()
  
  // State
  const [members, setMembers] = useState([])
  const [userProfiles, setUserProfiles] = useState({})
  const [loading, setLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [promptDismissed, setPromptDismissed] = useState(false)

  // Check permission
  const canManageMembers = can('manageMembers')
  
  // Show lifetime prompt only on first visit (once ever per user)
  const showLifetimePrompt = !monetizationLoading && 
    !promptDismissed && 
    shouldShowMembersPrompt()
  
  // Handle prompt dismissal
  const handleDismissPrompt = async () => {
    setPromptDismissed(true)
    await dismissMembersPrompt()
  }
  
  // Mark tab as visited when viewing (even if prompt not shown)
  useEffect(() => {
    if (!monetizationLoading && !shouldShowMembersPrompt()) {
      // Already visited before, ensure it's marked
      markMembersTabVisited()
    }
  }, [monetizationLoading, shouldShowMembersPrompt, markMembersTabVisited])

  // Fetch members on mount
  useEffect(() => {
    let mounted = true

    const fetchMembers = async () => {
      if (!projectId) return
      
      setLoading(true)
      setError(null)

      try {
        // Get all members
        const membersList = await getProjectMembers(projectId)
        
        if (!mounted) return
        
        setMembers(membersList)

        // Get user profiles for all members
        const userIds = membersList.map(m => m.userId)
        const profiles = await getUserProfiles(userIds)
        
        if (!mounted) return

        // Create a map of userId -> profile
        const profilesMap = {}
        profiles.forEach(profile => {
          if (profile) {
            profilesMap[profile.id] = profile
          }
        })
        
        setUserProfiles(profilesMap)
      } catch (err) {
        console.error('Failed to load members:', err)
        if (mounted) {
          setError(err.message || 'Failed to load members')
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    fetchMembers()

    return () => {
      mounted = false
    }
  }, [projectId])

  // Handle adding a new member
  const handleAddMember = async (userId, role) => {
    setActionLoading(true)
    
    try {
      await addProjectMember(projectId, userId, role, user.uid)
      
      // Refresh members list
      const membersList = await getProjectMembers(projectId)
      setMembers(membersList)

      // Fetch new user's profile
      const profiles = await getUserProfiles([userId])
      if (profiles[0]) {
        setUserProfiles(prev => ({
          ...prev,
          [profiles[0].id]: profiles[0]
        }))
      }
    } catch (err) {
      console.error('Failed to add member:', err)
      throw err
    } finally {
      setActionLoading(false)
    }
  }

  // Handle role change
  const handleRoleChange = async (userId, newRole) => {
    setActionLoading(true)
    
    try {
      await updateProjectMemberRole(projectId, userId, newRole)
      
      // Update local state
      setMembers(prev => prev.map(m => 
        m.userId === userId ? { ...m, role: newRole } : m
      ))
    } catch (err) {
      console.error('Failed to update role:', err)
      setError(err.message || 'Failed to update role')
    } finally {
      setActionLoading(false)
    }
  }

  // Handle member removal
  const handleRemoveMember = async (userId) => {
    setActionLoading(true)
    
    try {
      await removeProjectMember(projectId, userId)
      
      // Update local state
      setMembers(prev => prev.filter(m => m.userId !== userId))
    } catch (err) {
      console.error('Failed to remove member:', err)
      setError(err.message || 'Failed to remove member')
    } finally {
      setActionLoading(false)
    }
  }

  // Show access denied if user can't manage members
  if (!canManageMembers) {
    return <AccessDenied />
  }

  return (
    <motion.div 
      className="members-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <header className="members-view__header">
        <div className="members-view__title-group">
          <span className="members-view__icon">
            <Icon name="members" size={28} />
          </span>
          <h1 className="members-view__title">Project Members</h1>
        </div>
        <p className="members-view__subtitle">
          {loading 
            ? 'Loading members...' 
            : `${members.length} member${members.length !== 1 ? 's' : ''}`
          }
        </p>
        {project && (
          <p className="members-view__project-name">{project.name}</p>
        )}
      </header>
      
      {/* Lifetime Prompt - Shows once per user on first Members tab visit */}
      <LifetimePrompt
        variant="members"
        isVisible={showLifetimePrompt}
        onDismiss={handleDismissPrompt}
      />

      {/* Error State */}
      {error && (
        <GlassPanel glow="amber" padding="lg" className="members-view__error-panel">
          <h3>Error</h3>
          <p>{error}</p>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setError(null)}
          >
            Dismiss
          </Button>
        </GlassPanel>
      )}

      {/* Loading State */}
      {loading && !error && (
        <div className="members-view__loading">
          <div className="members-view__spinner" />
          <p>Loading members...</p>
        </div>
      )}

      {/* Members Table */}
      {!loading && !error && members.length > 0 && (
        <GlassPanel padding="none" className="members-view__table-container">
          {/* Table Header */}
          <div className="members-view__table-header">
            <div className="members-view__header-avatar"></div>
            <div className="members-view__header-user">User</div>
            <div className="members-view__header-role">Role</div>
            <div className="members-view__header-date">Added</div>
            <div className="members-view__header-actions"></div>
          </div>

          {/* Table Body */}
          <motion.div 
            className="members-view__table-body"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {members.map((member) => (
              <MemberRow
                key={member.userId}
                member={member}
                userProfile={userProfiles[member.userId]}
                isCurrentUser={member.userId === user?.uid}
                isProjectCreator={member.userId === project?.createdBy}
                onRoleChange={handleRoleChange}
                onRemove={handleRemoveMember}
                loading={actionLoading}
              />
            ))}
          </motion.div>
        </GlassPanel>
      )}

      {/* Empty State */}
      {!loading && !error && members.length === 0 && (
        <GlassPanel glow="cyan" padding="xl" className="members-view__empty">
          <div className="members-view__empty-icon"><Icon name="members" size={48} /></div>
          <h2>No members yet</h2>
          <p>Add your first team member to collaborate on this project.</p>
          <Button 
            variant="primary"
            leftIcon={<Icon name="plus" size={16} />}
            onClick={() => setModalOpen(true)}
          >
            Add Member
          </Button>
        </GlassPanel>
      )}

      {/* Floating Action Button */}
      {members.length > 0 && (
        <FloatingActionButton
          icon={<Icon name="plus" size={20} />}
          variant="primary"
          size="lg"
          position="bottom-right"
          label="Add member"
          onClick={() => setModalOpen(true)}
        />
      )}

      {/* Add Member Modal */}
      <AddMemberModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onAdd={handleAddMember}
        existingMemberIds={members.map(m => m.userId)}
      />
    </motion.div>
  )
}
